<template>
  <div class="h-100 d-flex flex-column align-center" :class="{ 'justify-space-around': !shouldFloat, 'float-container': shouldFloat }" ref="containerRef">
    <template v-for="(role, roleIndex) in system.roles" :key="role.id">
      <ConnectingLine
        :startElementId="`role-${system.index}-${role.id}`"
        :endElementId="`command-${system.index}-${roleIndex}`"
        :refreshKey="refreshKey"
        direction="auto"
        offsetParentId="domain-model"
      />
    </template>
    <div
      v-if="shouldFloat && aggRootInfo"
      class="sticky-float d-flex align-center justify-center"
      :style="{ top: aggRootInfo.top + 'px', height: aggRootInfo.height + 'px' }"
    >
      <v-card
        :id="`role-${system.index}-${role.id}`"
        class="fit-content role-card"
        ref="cardRef"
      >
        <div class="d-flex flex-column align-center">
          <v-icon class="my-2">mdi-human</v-icon>
          <bpmn-swim-lane-title
            class="px-3"
            :lane="role"
            :index="role.laneIndex"
            :svgGraph="svgGraph"
            :svgLanes="svgLanes"
            :workflow="workflow"
            disableReorder
            @name-blur="(e) => onRoleNameBlur(e, system, role.index)">
          </bpmn-swim-lane-title>
        </div>
      </v-card>
    </div>
    <v-card
      v-else
      :id="`role-${system.index}-${role.id}`"
      class="fit-content role-card"
      ref="cardRef"
    >
      <div class="d-flex flex-column align-center">
        <v-icon class="my-2">mdi-human</v-icon>
        <bpmn-swim-lane-title
          class="px-3"
          :lane="role"
          :index="role.laneIndex"
          :svgGraph="svgGraph"
          :svgLanes="svgLanes"
          :workflow="workflow"
          disableReorder
          @name-blur="(e) => onRoleNameBlur(e, system, role.index)">
        </bpmn-swim-lane-title>
      </div>
    </v-card>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, reactive, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import ConnectingLine from '@/components/ConnectingLine'
import BpmnSwimLaneTitle from '@/components/BpmnSwimLaneTitle'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'

const { system, role, refreshKey } = defineProps({ system: Object, role: Object, refreshKey: Number })

const store = useStore()
const route = useRoute()

const containerRef = ref(null)
const cardRef = ref(null)
const shouldFloat = ref(false)
const aggRootInfo = reactive({ top: 0, height: 0 })
let observer = null
let aggRootObserver = null

const STICKY_MARGIN = 50

const isSingleEventSelected = computed(() => !!route.params.eventId)
const workflow = computed(() => store.state.workflow.workflow)
const svgGraph = computed(() => safeStructuredClone(store.getters['svgGraphData/svgGraphData']))
const svgLanes = computed(() => safeStructuredClone(store.getters['svgGraphData/svgLanesData']))

const findAggregateRootStickyEl = () => {
  if (!containerRef.value) return null
  const tbody = containerRef.value.closest('tbody')
  if (!tbody) return null
  return tbody.querySelector(`#system-${system.index}`)
}

const syncAggRootInfo = () => {
  const aggRootEl = findAggregateRootStickyEl()
  if (!aggRootEl) return

  const top = parseInt(aggRootEl.style.top) || STICKY_MARGIN
  const height = aggRootEl.offsetHeight

  if (aggRootInfo.top !== top) aggRootInfo.top = top
  if (aggRootInfo.height !== height) aggRootInfo.height = height
}

const checkShouldFloat = () => {
  if (!containerRef.value) {
    return
  }
  shouldFloat.value = !isSingleEventSelected.value && containerRef.value.offsetHeight > window.innerHeight

  if (shouldFloat.value) {
    syncAggRootInfo()
  }
}

const onScroll = () => {
  if (!shouldFloat.value) return
  syncAggRootInfo()
}

const startObservingAggregateRoot = () => {
  if (aggRootObserver) {
    aggRootObserver.disconnect()
    aggRootObserver = null
  }

  const aggRootEl = findAggregateRootStickyEl()
  if (aggRootEl && typeof MutationObserver !== 'undefined') {
    aggRootObserver = new MutationObserver(() => {
      requestAnimationFrame(syncAggRootInfo)
    })
    aggRootObserver.observe(aggRootEl, { attributes: true, attributeFilter: ['style'] })
  }
}

const onRoleNameBlur = async (event, system, roleIndex) => {
  const currentRole = system.roles[roleIndex]
  const newRoleName = event.target.innerText

  if (newRoleName === currentRole.name) {
    return
  }

  if (newRoleName === '') {
    event.target.innerText = currentRole.name
    return
  }

  let events, updateNote
  const mergeWithRole = Object.values(svgLanes.value).find((lane) => lane.name === newRoleName)

  if (mergeWithRole) {
    events = system.events.filter((event) => event.laneId === currentRole.id)
    events.forEach((event) => {
      svgGraph.value[event.id].y = mergeWithRole.y + 30
      svgGraph.value[event.id].laneId = mergeWithRole.id
      svgGraph.value[event.id].laneIndex = mergeWithRole.index
    })
    updateNote = {
      action: 'moved events',
      target: events.map((it) => it.name).join(', ')
    }
  } else {
    svgLanes.value[currentRole.id].name = newRoleName
    updateNote = {
      action: 'updated lane name',
      target: newRoleName
    }
  }

  await workflowApi.updateWorkflow({
    svgGraph: svgGraph.value,
    svgLanes: svgLanes.value,
    workflow: workflow.value,
    updateNote
  }, true)
}

onMounted(() => {
  checkShouldFloat()
  if (containerRef.value) {
    observer = new ResizeObserver(() => {
      requestAnimationFrame(checkShouldFloat)
    })
    observer.observe(containerRef.value)
  }
  window.addEventListener('resize', checkShouldFloat, { passive: true })
  document.addEventListener('scroll', onScroll, { capture: true, passive: true })
  nextTick(startObservingAggregateRoot)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkShouldFloat)
  document.removeEventListener('scroll', onScroll, { capture: true })
  if (observer) {
    observer.disconnect()
  }
  if (aggRootObserver) {
    aggRootObserver.disconnect()
  }
})

watch(isSingleEventSelected, () => {
  nextTick(() => {
    checkShouldFloat()
  })
})

watch(shouldFloat, (val) => {
  if (val) {
    nextTick(startObservingAggregateRoot)
  }
})
</script>

<style scoped>
.float-container {
  padding-top: 150px;
  padding-bottom: 150px;
}

.sticky-float {
  position: sticky !important;
}

[data-theme="dark"] .role-card {
  border: 1px solid var(--light-4);
}
</style>
